import { EXCHANGES } from "@cede/utils";
import { MarketType, type HyperliquidMarket } from "@/hooks/trade/hyperliquid/types";

/**
 * The two unified-store setters a Hyperliquid market selection drives. Mirrors
 * the `useUnifiedTradeStore` signatures so a store change surfaces as a type
 * error at every call site that passes its setters in.
 */
export interface HyperliquidMarketSetters {
  setSelectedCexSpotMarket: (exchangeId: string, baseAsset: string, quoteAsset: string, pairSymbol: string) => void;
  setSelectedPerpsMarket: (
    symbol: string,
    exchangeId: string,
    baseAsset: string,
    quoteAsset: string,
    externalMarketId: string,
  ) => void;
}

/**
 * Resolve a base token to a concrete Hyperliquid market of the requested type.
 * Strict on type: a perp request never resolves to the spot market or vice
 * versa (base + type is unique on Hyperliquid). Returns undefined when absent;
 * callers that want a cross-type fallback should call twice and `??` the results.
 */
export function resolveHyperliquidMarket(
  allMarkets: HyperliquidMarket[],
  base: string,
  wantPerp: boolean,
): HyperliquidMarket | undefined {
  const wantedType = wantPerp ? MarketType.PERP : MarketType.SPOT;
  const upperBase = base.toUpperCase();
  return allMarkets.find((m) => m.type === wantedType && m.baseAsset.toUpperCase() === upperBase);
}

/**
 * Write a Hyperliquid market into the unified trade store, routing to the spot
 * or perps setter by market type. Single source of truth for "the user picked
 * this HL market".
 */
export function selectHyperliquidMarket(market: HyperliquidMarket, setters: HyperliquidMarketSetters): void {
  if (market.type === MarketType.SPOT) {
    setters.setSelectedCexSpotMarket(EXCHANGES.HYPERLIQUID, market.baseAsset, market.quoteAsset, market.symbol);
  } else {
    setters.setSelectedPerpsMarket(
      market.symbol,
      EXCHANGES.HYPERLIQUID,
      market.baseAsset,
      market.quoteAsset,
      market.symbol,
    );
  }
}
